<template>
  <div
    ref="rootElement"
    @keydown.esc.exact.prevent="destroyInstance()"
  >
    <FullCalendar
      ref="fullCalendarRef"
      data-cy="calendar-view"
      :style="colorOverrides"
      class="full-calendar-wrapper"
      :options="calendarOptions"
    />
  </div>
</template>

<script setup>
import dayjs from 'dayjs';
import { ref, computed, onMounted, getCurrentInstance, h, render } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useNuxtApp } from '#imports';
import FullCalendar from '@fullcalendar/vue3';
import dayGridViewPlugin from '@fullcalendar/daygrid';
import timeGridViewPlugin from '@fullcalendar/timegrid';
import listViewPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import EventPopUp from './EventPopUp';
import EventClockIcon from '../../icons/EventClockIcon.vue';
import ChevronBackIcon from '../../icons/ChevronBackIcon.vue';
import ChevronNextIcon from '../../icons/ChevronNextIcon.vue';
import { hexToRgb, blendAlpha } from '../../../../../helpers/colors';
import { TOOLTIP_POSITIONING, VIEW_TYPES, getSpecificListOrCalView } from '../../helpers/events.helper';

const MOBILE_WIDTH = 600;

defineOptions({ name: 'FullCalendarWrapper' });

const props = defineProps({
  events: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['change-view-type']);

const store = useStore();
const route = useRoute();
const { $t, $intl } = useNuxtApp();

const vm = getCurrentInstance();
const appContext = vm?.appContext;

const rootElement = ref(null);
const fullCalendarRef = ref(null);
/** @type {import('vue').Ref<import('@fullcalendar/core').CalendarApi | null>} */
const fullCalendarApi = ref(null);
const viewType = ref(VIEW_TYPES.MONTH);
const childInstance = ref(null);
const originalRef = ref(null);

const lastActiveEndDate = computed(() => store.state.events.lastActiveEndDate);
const queryParams = computed(() => store.state.events.queryParams);
const selectedYearUpdated = computed(() => store.state.events.selectedYearUpdated);
const calendarViewType = computed(() => store.state.events.calendarViewType);
const globals = computed(() => store.state.globals);
const customSections = computed(() => store.state.customSections);
const headingColor = computed(() => store.getters.getThemeHeadingColor);
const year = computed(() => store.getters['events/getCurrentYear']);
const date = computed(() => store.getters['events/getCurrentDate']);

const accessibleWeekButtonLabel = computed(() => {
  const startDate = dayjs(queryParams.value.start_date).format('MMMM-D-YYYY');
  const endDate = dayjs(queryParams.value.end_date).subtract(1, 'day').format('MMMM-D-YYYY');
  return `${startDate} - ${endDate}`;
});

const accessibleMonthButtonLabel = computed(() => dayjs(queryParams.value.start_date).format('MMMM'));

const colorOverrides = computed(() => {
  const color = headingColor.value;
  if (!color) {
    return {};
  }

  const targetRGB = { ...hexToRgb(color), a: 0.1 };
  const backgroundRGB = blendAlpha({ ...hexToRgb(color), a: 0.08 });

  return {
    '--button-outline': color,
    '--button-primary-bg': color,
    '--button-primary-bg-hover': color,
    '--table-border': `${color}1A`,
    '--event-bg': `${blendAlpha(targetRGB, true, backgroundRGB)}`,
    '--today-highlight-color': `${color}14`,
    '--month-week-border-color': `${color}4D`,
    '--month-week-bg-active': `${color}1A`,
    '--border-events': `1px solid ${color}`,
    '--month-week-width': viewType.value === VIEW_TYPES.WEEK ? '315px' : '215px',
  };
});

function getFollowingMonth(currStartYear, startMonth) {
  return {
    startDate: new Date(currStartYear, startMonth + 1, 1).toISOString().split('T')[0],
    endDate: new Date(currStartYear, startMonth + 2, 0).toISOString().split('T')[0],
  };
}

function eventTags(event) {
  const tags = [];
  const section = customSections.value.find((sec) => sec.id === event.custom_section_id);
  if (section) tags.push(section.name);
  tags.push(...event.filter_name);
  return tags;
}

function destroyInstance() {
  if (originalRef.value) {
    render(null, originalRef.value);
    childInstance.value = null;
    originalRef.value = null;
  }
}

function createSVGClockInstance() {
  const instance = h(EventClockIcon);
  if (appContext) instance.appContext = appContext;
  return instance;
}

function createSVGChevronInstance(useNextIcon = false) {
  const instance = h(useNextIcon ? ChevronNextIcon : ChevronBackIcon);
  if (appContext) instance.appContext = appContext;
  return instance;
}

function todayButtonAction() {
  const todaysDate = new Date();
  const payload = {
    start: todaysDate,
    end: todaysDate,
    view: {
      currentStart: todaysDate,
      currentEnd: todaysDate,
    },
  };
  handleChangeDate(payload, true);
}

function dateChanged(payload) {
  if (viewType.value === 'timeGridWeek') {
    return date.value.toISOString() !== payload.start.toISOString();
  }
  return date.value.getMonth() !== payload.view.currentStart.getMonth();
}

function handleChangeDate(payload, todayAction) {
  store.commit('events/setCalViewType', viewType.value);
  if (dateChanged(payload) || todayAction) {
    store.commit('events/setLastActiveEndDate', payload.view.activeEnd);
    const dayNum = payload.view.currentStart.getDate();
    const startMonth = payload.view.currentStart.getMonth();
    const endMonth = payload.view.currentStart.getMonth() + 1;
    let currStartYear = payload.view.currentStart.getFullYear();
    let startDate = new Date(currStartYear, startMonth, 1).toISOString().split('T')[0];
    let endDate = new Date(currStartYear, endMonth, 0).toISOString().split('T')[0];
    if (viewType.value === 'timeGridWeek') {
      if (selectedYearUpdated.value) {
        store.commit('events/setSelectedYearUpdated', false);
        currStartYear = year.value;
      }
      startDate = new Date(currStartYear, payload.start.getMonth(), payload.start.getDate())
        .toISOString()
        .split('T')[0];
      endDate = new Date(payload.end.getFullYear(), payload.end.getMonth(), payload.end.getDate())
        .toISOString()
        .split('T')[0];
    }
    store.commit('events/setCurrentDate', {
      year: currStartYear,
      month: startMonth,
      day: dayNum,
    });
    return navigateTo({
      query: {
        ...route.query,
        ...queryParams.value,
        start_date: startDate,
        end_date: endDate,
        date: undefined,
        id: undefined,
        view: getSpecificListOrCalView(store.state.events),
      },
    });
  }
}

function handleEventClick($event) {
  const { jsEvent, event, el } = $event;
  jsEvent.preventDefault();
  jsEvent.stopPropagation();

  if (childInstance.value && event.extendedProps.id === childInstance.value.props?.event?.id) {
    destroyInstance();
    return;
  }

  if (childInstance.value) {
    destroyInstance();
  }

  const eventData = event.extendedProps;

  let windowWidth = null;
  if (import.meta.client) {
    windowWidth = window.innerWidth;
  }

  if (windowWidth < MOBILE_WIDTH) {
    return;
  }
  createNewInstance(eventData, el);
}

function createNewInstance(event, el) {
  const elRoot = rootElement.value;
  if (!elRoot) return;

  const rect = elRoot.getBoundingClientRect();
  const parentBoundingBox = {
    element: elRoot,
    top: rect.top + window.scrollY,
    bottom: rect.bottom + window.scrollY,
    left: rect.left + window.scrollX,
    right: rect.right + window.scrollX,
    width: rect.width,
    height: rect.height,
    x: rect.x + window.scrollX,
    y: rect.y + window.scrollY,
  };
  const elementBounding = el.getBoundingClientRect();
  const originalRefNode = viewType.value === VIEW_TYPES.WEEK ? el.parentNode.parentNode : el.parentNode;
  const instance = h(EventPopUp, {
    event: {
      ...event,
      eventTags: eventTags(event),
      type: 'event',
    },
    getGlobals: globals.value,
    tooltipPosition: TOOLTIP_POSITIONING(el.parentNode, elRoot, viewType.value),
    parentBoundingBox: parentBoundingBox,
    colorOverrides: colorOverrides.value,
    eventYPos: elementBounding.top + elementBounding.height / 2,
    viewType: calendarViewType.value,
    onClickedAway: () => destroyInstance(),
    onCloseEsc: () => el.focus(),
    onViewEvent: ($event) => viewEvent($event),
  });
  if (appContext) instance.appContext = appContext;
  childInstance.value = instance;
  originalRef.value = originalRefNode;
  render(instance, originalRefNode);
}

function viewEvent(id) {
  store.commit('events/changeEventView', 'LIST_VIEW');
  return navigateTo({
    query: {
      ...route.query,
      view: undefined,
      id,
    },
  });
}

function handleViewDidMount({ view }) {
  if (viewType.value === view.type) return;

  viewType.value = view.type;
  store.commit('events/setCalViewType', viewType.value);

  if (fullCalendarApi.value) {
    fullCalendarApi.value.setOption('height', view.type === 'dayGridMonth' ? null : 'auto');
  }
  emit('change-view-type', view.type);
  if (view.type === 'dayGridMonth') {
    const dayNum = view.currentStart.getDate();
    const startMonth = view.currentStart.getMonth();
    let endMonth = view.activeEnd.getMonth();
    const currStartYear = view.currentStart.getFullYear();

    if (lastActiveEndDate.value) {
      endMonth = lastActiveEndDate.value.getMonth();
    }

    const rangeDates =
      endMonth > startMonth
        ? getFollowingMonth(currStartYear, startMonth)
        : {
            startDate: view.currentStart.toISOString().split('T')[0],
            endDate: view.currentEnd.toISOString().split('T')[0],
          };

    store.commit('events/setCurrentDate', {
      year: currStartYear,
      month: startMonth,
      day: dayNum,
    });
    return navigateTo({
      query: {
        ...route.query,
        ...queryParams.value,
        start_date: rangeDates.startDate,
        end_date: rangeDates.endDate,
        date: undefined,
        id: undefined,
        view: getSpecificListOrCalView(store.state.events),
      },
    });
  }
}

const calendarOptions = computed(() => ({
  locale: $intl.locale,
  plugins: [dayGridViewPlugin, timeGridViewPlugin, listViewPlugin, interactionPlugin],
  initialView: calendarViewType.value || 'dayGridMonth',
  headerToolbar: {
    left: 'customLeft,title,customRight',
    center: '',
    right: 'customToday dayGridMonth,timeGridWeek',
  },
  buttonText: {
    today: $t('events.today'),
  },
  events: props.events,
  eventOrder: 'start_at',
  eventClick: handleEventClick,
  viewDidMount: handleViewDidMount,
  datesSet: handleChangeDate,
  views: {
    dayGridMonth: {
      buttonText: $t('events.month'),
    },
    timeGridWeek: {
      buttonText: $t('events.week'),
      allDaySlot: true,
      allDayText: $t('events.allDayUppercase'),
      slotMinWidth: '500px',
    },
  },
  customButtons: {
    customToday: {
      text: 'Today',
      click: () => {
        todayButtonAction();
      },
    },
    customLeft: {
      text:
        viewType.value === 'timeGridWeek'
          ? $t('events.previousWeek', { accessibleWeek: accessibleWeekButtonLabel.value })
          : $t('events.previousMonth', { accessibleMonth: accessibleMonthButtonLabel.value }),
      icon: 'chevron-left',
      click: () => {
        fullCalendarApi.value?.prev();
      },
    },
    customRight: {
      text:
        viewType.value === 'timeGridWeek'
          ? $t('events.nextWeek', { accessibleWeek: accessibleWeekButtonLabel.value })
          : $t('events.nextMonth', { accessibleMonth: accessibleMonthButtonLabel.value }),
      icon: 'chevron-right',
      click: () => {
        fullCalendarApi.value?.next();
      },
    },
  },
  height: 'auto',
  initialDate: date.value,
  eventDidMount: (info) => {
    const { el, event, view } = info;
    el.href = 'javascript:void(0);';
    el.onfocus = destroyInstance;
    if (view.type === VIEW_TYPES.WEEK) {
      const { formatted_start, formatted_end, all_day } = event.extendedProps;
      const start = dayjs(formatted_start);
      const end = dayjs(formatted_end);
      const isMultiDay = start.date() !== end.date();
      const eventTitleNode = el.querySelector('.fc-event-title');
      if (isMultiDay && !all_day) {
        const clockIconInstance = createSVGClockInstance();
        render(clockIconInstance, eventTitleNode);
      }

      if (event.allDay) {
        const viewStartDate = dayjs(view.currentStart);
        const viewEndDate = dayjs(view.currentEnd);

        if (start.isBefore(viewStartDate)) {
          const chevronIconInstance = createSVGChevronInstance(false);
          render(chevronIconInstance, eventTitleNode);
        }

        if (end.isAfter(viewEndDate)) {
          const chevronIconInstance = createSVGChevronInstance(true);
          render(chevronIconInstance, eventTitleNode);
        }
      }
    }
  },
  allDayContent: '',
}));

onMounted(() => {
  fullCalendarApi.value = fullCalendarRef.value?.getApi?.() ?? null;
  document.querySelectorAll('.fc-icon').forEach((elem) => {
    elem.tabIndex = -1;
  });
});
</script>

<style lang="scss" scoped>
:deep(.fc) {
  .fc-scrollgrid-section-sticky > * {
    z-index: auto;
  }
  .fc-timegrid-event-harness {
    .fc-timegrid-event {
      min-height: 24px;
    }
  }
  .fc-event {
    .fc-event-title-container {
      flex-grow: 0;
      flex-shrink: 0;
    }
    .fc-event-main {
      background-color: var(--event-bg);

      .fc-event-main-frame {
        flex-direction: column-reverse;
        justify-content: flex-end;
        overflow: hidden;
      }
    }
  }
  .fc-scroller-harness,
  .fc-scroller-liquid-absolute,
  .fc-scroller {
    overflow: visible !important;
  }
  .fc-day:has(.cal-event-popup) {
    z-index: 99;
    position: relative;
  }
}

@mixin cal-button-override {
  background: transparent;
  border: none;
}

@mixin cal-font-override {
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;

  display: flex;
  align-items: center;
  text-align: center;
  text-transform: capitalize;
}

.view-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;

  .view-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 24px;

    .view-header-left,
    .view-header-right {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
    }
  }

  .full-calendar-wrapper {
    flex: 1;
  }
}

.full-calendar-wrapper {
  margin-top: 32px;

  & :deep() {
    --fc-event-text-color: #333;
    --fc-event-border-color: var(--event-bg);
    --fc-event-bg-color: var(--event-bg);

    --fc-today-bg-color: var(--today-highlight-color);

    --fc-border-color: var(--table-border);
    --fc-button-active-border-color: var(--month-week-border-color);

    .fc-dayGridMonth-view {
      list-style: none;
    }
    & .fc-view-harness {
      min-height: 300px;
    }

    & .fc-timegrid-axis-cushion {
      max-width: none;
      min-width: 100px;
      text-align: center;
    }

    & .fc-daygrid-day-top {
      flex-direction: initial;
      padding-left: 8px;
      font-weight: 500;
    }

    & .fc-col-header-cell-cushion {
      font-weight: 400;
    }

    & .fc-daygrid {
      & .fc-col-header-cell-cushion {
        float: right;
      }
    }

    & .fc-timegrid-slot-label-frame {
      text-align: center;
      text-transform: uppercase;
    }

    & .fc-header-toolbar {
      min-height: 90px;
      padding: 24px;
      border: 1px solid rgba(0, 73, 144, 0.1);
      border-radius: 3px;
      margin: 0;

      @media (max-width: 424px) {
        flex-direction: column;
      }
    }

    & .fc-toolbar-chunk {
      &:first-of-type {
        & div {
          flex: 1;
          display: flex;
          align-items: center;
        }

        & > * {
          & > .fc-toolbar-title {
            font-style: normal;
            font-weight: 500;
            font-size: 24px;
            line-height: 23px;
            width: var(--month-week-width);
            text-align: center;
            color: #333333;
          }

          & > .fc-button {
            @include cal-button-override;
            color: #00376c; /* TODO: Should be Dynamic*/
          }
        }
      }
      &:last-of-type {
        display: flex;
        & > .fc-button {
          @include cal-button-override;
          @include cal-font-override;
          border: 1px solid rgba(0, 73, 144, 0.3);
          border-radius: 4px;
          color: #333333;
          padding: 12px;
          border: 1px solid var(--month-week-border-color);
        }

        & > .fc-button-group {
          border: 1px solid rgba(0, 73, 144, 0.3);
          border-radius: 4px;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 8px;
          margin-left: 8px;
          border: 1px solid var(--month-week-border-color);
        }

        & > * {
          & > .fc-button {
            @include cal-button-override;
            @include cal-font-override;
            color: #333333;

            border-radius: 4px;
            height: 20px;
            &.fc-button-active {
              background: var(--month-week-bg-active);
            }
          }
        }
      }
    }
    & .fc-button.fc-button-primary {
      &.fc-next-button,
      &.fc-prev-button {
        padding: 0;
        & .fc-icon {
          &:focus {
            outline: none;
            box-shadow: none;
          }
        }
        &:focus {
          outline: none;
          box-shadow: none;
          .fc-icon {
            box-shadow: 0 0 0 0.2rem #4c5b6a80;
          }
        }
      }
      &.fc-dayGridMonth-button,
      &.fc-timeGridWeek-button {
        &:focus {
          box-shadow: none;
        }
        &:focus-visible {
          box-shadow: 0 0 0 0.2rem #4c5b6a80;
        }
      }
    }
    &
      > .fc-view-harness
      > div
      > table
      > tbody
      > tr
      > td
      > div
      > div
      > div
      > table
      > tbody
      > tr
      > td.fc-daygrid-day
      > div
      > div.fc-daygrid-day-events
      > div {
      cursor: pointer;

      & > a > div.fc-event-main > div.fc-event-main-frame > div.fc-event-title-container > div.fc-event-title {
        &::marker {
          margin-right: 8px;
        }

        display: list-item;
        list-style-position: inside;
        margin-left: 8px;
      }
    }
  }
}
</style>
